import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Users, AlarmClock, X } from "lucide-react";
import { api } from "../../lib/api";
import { formatINR, formatDate } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import StatusBadge from "../../components/StatusBadge";
import { EditModeToggle } from "../../components/editor/EditModeToggle";
import { useEditModeSession } from "../../hooks/useEditModeSession";
import LeaderboardWidget from "../../components/LeaderboardWidget";
import MilestoneProgressWidget from "../../components/MilestoneProgressWidget";

function SlotsWidget() {
  // Slots widget hidden — unlimited agreements now available
  return null;
}

function Stat({ label, value, sub, color = "text-gray-900" }) {
  return (
    <div className="card-base p-5">
      <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
      <div className={`mt-2 text-3xl font-bold font-display ${color}`}>{value}</div>
      <div className="text-xs text-gray-500 mt-1">{sub}</div>
    </div>
  );
}

export default function BrokerHome() {
  const { user } = useAuth();
  const { isInEditMode } = useEditModeSession();
  const [rentals, setRentals] = useState([]);
  const [team, setTeam] = useState([]);
  const [showWelcome, setShowWelcome] = useState(localStorage.getItem("orello_welcome_dismissed_admin") !== "1");

  useEffect(() => {
    (async () => {
      const [r, t] = await Promise.all([api.get("/rentals"), api.get("/team")]);
      setRentals(r.data); setTeam(t.data);
    })();
  }, []);

  const active = rentals.filter(r => ["active","checkout_pending","rating_phase1","settlement_pending","rating_phase2"].includes(r.status)).length;
  const monthVol = rentals.filter(r => r.status === "active" || r.status === "completed").reduce((s, r) => s + (r.deposit_amount || 0), 0);
  const expiring = rentals.filter(r => {
    if (!r.lease_end_date) return false;
    const d = Math.ceil((new Date(r.lease_end_date) - new Date()) / 86400000);
    return d > 0 && d <= 30;
  }).length;

  const dismiss = () => { localStorage.setItem("orello_welcome_dismissed_admin", "1"); setShowWelcome(false); };

  return (
    <div className={isInEditMode ? "edit-mode-active" : ""}>
      <EditModeToggle pageId="broker_dashboard" />

      <div className="space-y-6" data-testid="broker-admin-home">
        {showWelcome && (
        <div className="rounded-xl bg-gray-800 border border-orange-500 p-5 relative banner-hover" data-testid="welcome-banner">
          <button onClick={dismiss} className="absolute top-3 right-3 text-gray-400 hover:text-gray-300"><X className="h-4 w-4" /></button>
          <h2 className="text-lg font-semibold font-display text-white">Welcome to Orello, {user?.name?.split(" ")[0]}!</h2>
          <p className="text-sm text-gray-400 mt-1">You're all set. Add your first agent and start creating deals.</p>
          <button onClick={() => window.location.href = "/app/team"} className="inline-flex mt-3 bg-[#F97316] hover:bg-[#EA580C] btn-press btn-orange-primary text-white px-4 py-2 rounded-lg text-sm font-medium">Add an Agent →</button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display">Home</h1>
          <p className="text-sm text-gray-500 mt-1">Your agency at a glance.</p>
        </div>
        <Link to="/app/agreement-builder" className="bg-[#F97316] hover:bg-[#EA580C] btn-press btn-orange-primary text-white px-4 py-2.5 rounded-lg text-sm font-medium inline-flex items-center gap-2"><Plus className="h-4 w-4" /> New Rental</Link>
      </div>

      <LeaderboardWidget />

      <MilestoneProgressWidget />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat label="Active Deals" value={active} sub="deals running right now" />
        <Stat label="My Agents" value={team.length} sub="verified agents on your team" />
        <Stat label="This Month" value={formatINR(monthVol)} sub="in deposits secured" />
        <Stat label="Expiring Soon" value={expiring} sub="leases in next 30 days" color={expiring > 0 ? "text-amber-600" : "text-gray-900"} />
      </div>

      {/* Slots Widget */}
      <SlotsWidget />

      <div className="card-base">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold">Your Team</h2>
          <Link to="/app/team" className="bg-[#F97316] hover:bg-[#EA580C] btn-press btn-orange-primary text-white px-3 py-1.5 rounded-lg text-xs font-medium inline-flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> Add Agent</Link>
        </div>
        {team.length === 0 ? (
          <div className="p-10 text-center" data-testid="team-empty">
            <Users className="h-10 w-10 mx-auto text-gray-300" />
            <p className="mt-3 text-gray-800 font-medium">No agents yet.</p>
            <p className="text-sm text-gray-600">Add your first agent and they'll start creating deals for you.</p>
            <Link to="/app/team" className="inline-flex mt-4 bg-[#F97316] hover:bg-[#EA580C] btn-orange-primary text-white px-4 py-2 rounded-lg text-sm font-medium">Add Agent +</Link>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[11px] text-gray-500 uppercase tracking-wider bg-gray-50">
              <tr>
                <th className="text-left px-5 py-3">Agent</th>
                <th className="text-left px-5 py-3">Total Deals</th>
                <th className="text-left px-5 py-3">Active Deals</th>
                <th className="text-left px-5 py-3">Joined</th>
                <th className="text-left px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {team.map(a => (
                <tr key={a.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-7 w-7 rounded-full bg-[#F97316] text-white flex items-center justify-center text-xs font-semibold">{a.name?.[0]?.toUpperCase()}</div>
                      <div>
                        <div className="font-medium">{a.name}</div>
                        <div className="text-[11px] text-gray-500">{a.phone}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">{a.deals_total}</td>
                  <td className="px-5 py-3">{a.deals_active}</td>
                  <td className="px-5 py-3 text-gray-600">{formatDate(a.created_at)}</td>
                  <td className="px-5 py-3"><span className={`pill ${a.verified ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{a.verified ? "Active" : "Pending"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card-base p-5">
        <h3 className="font-semibold flex items-center gap-2"><AlarmClock className="h-4 w-4 text-amber-500" /> Coming up</h3>
        {rentals.filter(r => {
          if (!r.lease_end_date) return false;
          const d = Math.ceil((new Date(r.lease_end_date) - new Date()) / 86400000);
          return d > 0 && d <= 60;
        }).length === 0 ? (
          <p className="text-sm text-gray-500 mt-2">All good. Nothing due right now.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {rentals.filter(r => { if (!r.lease_end_date) return false; const d = Math.ceil((new Date(r.lease_end_date) - new Date()) / 86400000); return d > 0 && d <= 60; }).slice(0,5).map(r => {
              const days = Math.ceil((new Date(r.lease_end_date) - new Date()) / 86400000);
              return <li key={r.id} className="border-l-4 border-amber-300 bg-amber-50/40 px-3 py-2 text-sm">{r.agent?.name}'s deal at {r.property_address} expires in {days} days</li>;
            })}
          </ul>
        )}
      </div>
      </div>
    </div>
  );
}